import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, KeyRound, ArrowLeft } from 'lucide-react';
import { authService } from '@/core/api';
import { useTelegram, useStore } from '@/core/providers';
import { LoadingSpinner } from '@/components';

function LoginPage() {
  const navigate = useNavigate();
  const { hapticFeedback, showAlert, user: tgUser, initData } = useTelegram();
  const { setUser } = useStore();

  const [authMethod, setAuthMethod] = useState<'password' | 'code'>('code');
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    console.log('=== LoginPage Mount Debug ===');
    console.log('tgUser:', tgUser);
    console.log('initData:', initData);
    console.log('Has tgUser:', !!tgUser);
    console.log('Has initData:', !!initData);
    console.log('Button will be rendered:', !!tgUser);
  }, [tgUser, initData]);

  const handleSendCode = async () => {
    if (!email) {
      setError('Please enter your email');
      return;
    }

    setIsLoading(true);
    setError(null);
    hapticFeedback('impact');

    try {
      await authService.sendVerificationCode(email, mode);
      setCodeSent(true);
      setCountdown(60);
      hapticFeedback('notification');
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to send verification code';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (authMethod === 'password') {
      if (!email || !password) {
        setError('Please fill in all fields');
        return;
      }

      setIsLoading(true);
      setError(null);
      hapticFeedback('impact');

      try {
        const response =
          mode === 'login'
            ? await authService.login(email, password)
            : await authService.register(email, password);

        localStorage.setItem('access_token', response.access_token);
        localStorage.setItem('refresh_token', response.refresh_token);
        setUser(response.user);

        hapticFeedback('notification');
        navigate('/feed');
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : mode === 'login'
            ? 'Invalid email or password'
            : 'Registration failed';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Code-based auth
      if (!email || !code) {
        setError('Please enter email and verification code');
        return;
      }

      setIsLoading(true);
      setError(null);
      hapticFeedback('impact');

      try {
        const response = await authService.verifyCode(email, code, mode);

        localStorage.setItem('access_token', response.access_token);
        localStorage.setItem('refresh_token', response.refresh_token);
        setUser(response.user);

        hapticFeedback('notification');
        navigate('/feed');
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : 'Invalid verification code';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleTelegramLogin = async () => {
    console.log('=== Telegram Login Debug ===');
    console.log('initData:', initData);
    console.log('tgUser:', tgUser);
    console.log('initData exists:', !!initData);
    console.log('tgUser exists:', !!tgUser);

    if (!initData || !tgUser) {
      const msg = `Telegram login is not available. initData: ${!!initData}, tgUser: ${!!tgUser}`;
      console.error(msg);
      showAlert(msg);
      return;
    }

    setIsLoading(true);
    setError(null);
    hapticFeedback('impact');

    try {
      console.log('Calling authService.telegramAuth with initData:', initData.substring(0, 50) + '...');
      const response = await authService.telegramAuth(initData);
      console.log('Auth response received:', response);

      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      setUser(response.user);

      hapticFeedback('notification');
      console.log('Navigating to /feed');
      navigate('/feed');
    } catch (err) {
      console.error('Telegram login error:', err);
      const errorMsg = err instanceof Error ? err.message : 'Telegram login failed';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-telegram-bg flex flex-col">
      {/* Back Button */}
      <div
        className="pointer-events-none fixed left-1/2 z-30 -translate-x-1/2"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <div className="pointer-events-auto flex items-center gap-2 rounded-full bg-[var(--ff-surface)]/90 px-2 py-1 shadow-[0_12px_28px_rgba(0,0,0,0.08)] backdrop-blur">
          <button
            onClick={() => navigate(-1)}
            className="rounded-full border border-[var(--ff-border)] bg-[var(--ff-surface-alt)] px-3 py-2 text-sm font-semibold text-telegram-text shadow-sm hover:shadow transition"
            aria-label="Go back"
          >
            <ArrowLeft size={18} />
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="px-4 py-8 pt-20 text-center">
        <h1 className="text-3xl font-bold text-telegram-text mb-2">
          Feed Flow
        </h1>
        <p className="text-telegram-hint">
          Track anything with natural language
        </p>
      </div>

      {/* Form */}
      <div className="flex-1 px-4">
        {/* Auth Method Toggle */}
        <div className="flex gap-2 p-1 bg-telegram-secondary rounded-lg mb-6">
          <button
            type="button"
            onClick={() => {
              setAuthMethod('code');
              setError(null);
              hapticFeedback('selection');
            }}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              authMethod === 'code'
                ? 'bg-telegram-button text-white'
                : 'text-telegram-hint'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <KeyRound size={16} />
              Verification Code
            </div>
          </button>
          <button
            type="button"
            onClick={() => {
              setAuthMethod('password');
              setError(null);
              hapticFeedback('selection');
            }}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              authMethod === 'password'
                ? 'bg-telegram-button text-white'
                : 'text-telegram-hint'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Lock size={16} />
              Password
            </div>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-telegram-text mb-1">
              Email
            </label>
            <div className="relative">
              <Mail
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-telegram-hint"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setCodeSent(false);
                }}
                placeholder="your@email.com"
                className="w-full pl-10 pr-4 py-3 bg-telegram-secondary rounded-lg text-telegram-text placeholder-telegram-hint focus:outline-none focus:ring-2 focus:ring-telegram-button"
              />
            </div>
          </div>

          {authMethod === 'password' ? (
            /* Password */
            <div>
              <label className="block text-sm font-medium text-telegram-text mb-1">
                Password
              </label>
              <div className="relative">
                <Lock
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-telegram-hint"
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-12 py-3 bg-telegram-secondary rounded-lg text-telegram-text placeholder-telegram-hint focus:outline-none focus:ring-2 focus:ring-telegram-button"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-telegram-hint"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          ) : (
            /* Verification Code */
            <>
              <div>
                <label className="block text-sm font-medium text-telegram-text mb-1">
                  Verification Code
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <KeyRound
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-telegram-hint"
                    />
                    <input
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                      className="w-full pl-10 pr-4 py-3 bg-telegram-secondary rounded-lg text-telegram-text placeholder-telegram-hint focus:outline-none focus:ring-2 focus:ring-telegram-button"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={isLoading || countdown > 0}
                    className="px-4 py-3 bg-telegram-secondary text-telegram-text rounded-lg font-medium disabled:opacity-50 hover:bg-opacity-80 transition-colors whitespace-nowrap"
                  >
                    {countdown > 0 ? `${countdown}s` : codeSent ? 'Resend' : 'Send'}
                  </button>
                </div>
                {codeSent && countdown > 0 && (
                  <p className="text-xs text-telegram-hint mt-1">
                    Code sent! Check your email.
                  </p>
                )}
              </div>
            </>
          )}

          {/* Error */}
          {error && (
            <p className="text-sm text-red-500 text-center">{error}</p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading || (authMethod === 'code' && !codeSent)}
            className="w-full btn-primary py-3 flex items-center justify-center disabled:opacity-50"
          >
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : mode === 'login' ? (
              'Sign In'
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        {/* Telegram Login */}
        {tgUser && (
          <div className="mt-4">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-telegram-secondary" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-telegram-bg text-telegram-hint">
                  or
                </span>
              </div>
            </div>

            <button
              onClick={() => {
                console.log('Telegram button clicked!');
                handleTelegramLogin();
              }}
              disabled={isLoading}
              className="mt-4 w-full bg-telegram-button text-telegram-button-text py-3 rounded-lg font-medium flex items-center justify-center gap-2"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.02-1.96 1.25-5.54 3.69-.52.36-1 .53-1.42.52-.47-.01-1.37-.26-2.03-.48-.82-.27-1.47-.42-1.42-.88.03-.25.38-.51 1.07-.78 4.18-1.82 6.97-3.02 8.38-3.61 3.99-1.66 4.82-1.95 5.36-1.96.12 0 .37.03.54.18.14.13.18.3.2.45-.01.06.01.24 0 .38z" />
              </svg>
              Continue with Telegram
            </button>
          </div>
        )}

        {/* Toggle mode */}
        <div className="mt-6 text-center">
          <button
            onClick={() => {
              hapticFeedback('selection');
              setMode(mode === 'login' ? 'register' : 'login');
              setError(null);
            }}
            className="text-sm text-telegram-link"
          >
            {mode === 'login'
              ? "Don't have an account? Sign up"
              : 'Already have an account? Sign in'}
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 text-center text-xs text-telegram-hint">
        <p>By continuing, you agree to our Terms and Privacy Policy</p>
      </div>
    </div>
  );
}

export default LoginPage;
